import React, { useEffect, useRef } from 'react';
import p5 from 'p5';

export const ParticleSystem: React.FC<{
  containerRef: React.RefObject<HTMLDivElement | HTMLElement | null>;
}> = ({ containerRef }) => {
  const sketchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only proceed if both refs are available
    if (!sketchRef.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const sketch = (p: p5) => {
      const particles: Particle[] = [];
      const themeColors = ['#0E5CF4', '#54ADE8', '#2C4B89'];

      class Particle {
        position: p5.Vector;
        velocity: p5.Vector;
        size: number;
        color: p5.Color;
        shape: number;
        friction: number;
        alpha: number;

        constructor() {
          // Initialize particles within the modal container
          this.position = p.createVector(p.random(containerWidth), p.random(-50, -10));
          this.velocity = p.createVector(p.random(-6, 6), p.random(-10, 2));
          this.size = p.random(5, 15);
          this.color = p.color(p.random(themeColors));
          this.shape = p.round(p.random(0, 1));
          this.friction = p.random(0.995, 0.98);
          this.alpha = 255; // Full opacity
        }

        update() {
          this.velocity.add(p.createVector(0, 0.1)); // Gravity
          this.velocity.mult(this.friction);
          this.position.add(this.velocity);

          // Reset if particle goes outside the modal container boundaries
          if (
            this.position.y > containerHeight ||
            this.position.x < 0 ||
            this.position.x > containerWidth
          ) {
            this.reset();
          }
        }

        reset() {
          this.position = p.createVector(p.random(containerWidth), p.random(-50, -10));
          this.velocity = p.createVector(p.random(-6, 6), p.random(-10, 2));
        }

        fadeOut() {
          this.alpha = Math.max(this.alpha - 5, 0); // Gradually reduce opacity
        }

        draw() {
          p.noStroke();
          p.fill(p.red(this.color), p.green(this.color), p.blue(this.color), this.alpha);
          p.push();
          p.translate(this.position.x, this.position.y);
          p.rotate(this.velocity.x * 2);
          p.scale(1, 0.5 + Math.sin(this.velocity.y * 20) * 0.5);

          if (this.shape === 0) {
            p.rect(-this.size / 2, -this.size / 2, this.size, this.size);
          } else {
            p.ellipse(0, 0, this.size, this.size);
          }

          p.pop();
        }
      }

      p.setup = () => {
        // Create canvas with the size of the modal container
        p.createCanvas(containerWidth, containerHeight);
        for (let i = 0; i < 100; i++) {
          particles.push(new Particle());
        }
      };

      p.draw = () => {
        p.clear();
        particles.forEach((particle) => {
          particle.update();
          particle.draw();
        });
      };

      p.windowResized = () => {
        // Get updated container dimensions if window resizes
        if (containerRef.current) {
          const updatedRect = containerRef.current.getBoundingClientRect();
          p.resizeCanvas(updatedRect.width, updatedRect.height);
        }
      };

      const fadeOutParticles = () => {
        const fadeInterval = setInterval(() => {
          particles.forEach((particle) => particle.fadeOut());
          p.redraw();

          // Stop fading when all particles are fully transparent
          if (particles.every((particle) => particle.alpha === 0)) {
            clearInterval(fadeInterval);
            p5Instance.remove();
          }
        }, 50); // Gradual fade every 50ms
      };

      const timeoutId = setTimeout(() => {
        fadeOutParticles();
      }, 5000);

      return () => {
        clearTimeout(timeoutId);
        p5Instance.remove();
      };
    };

    const p5Instance = new p5(sketch, sketchRef.current);
  }, [containerRef]);

  return (
    <div
      ref={sketchRef}
      className='absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none'
    />
  );
};
